import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { productsAPI, cartAPI, authAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Star, SlidersHorizontal, X } from 'lucide-react';
import Layout from '@/components/Layout';
import { toast } from 'sonner';
import { hintText } from '@/lib/utils';

const ProductsPage = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState('all');
  const [brand, setBrand] = useState('all');
  const [brands, setBrands] = useState([]);
  const [priceRange, setPriceRange] = useState([0, 500000]);
  const [minRating, setMinRating] = useState(0);
  const [sortBy, setSortBy] = useState('featured');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    loadBrands();
  }, []);

  useEffect(() => {
    loadProducts();
    checkAuth();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [category, brand, priceRange, minRating, sortBy]);

  const loadBrands = async () => {
    try {
      const data = await productsAPI.getBrands();
      setBrands(data);
    } catch (error) {
      console.error('Error loading brands:', error);
    }
  };

  const checkAuth = async () => {
    try {
      await authAPI.getMe();
      setIsAuthenticated(true);
    } catch (error) {
      setIsAuthenticated(false);
    }
  };

  const loadProducts = async () => {
    try {
      setLoading(true);
      const params = {};
      if (category !== 'all') params.category = category;
      if (brand !== 'all') params.brand = brand;
      if (priceRange[0] > 0) params.min_price = priceRange[0];
      if (priceRange[1] < 500000) params.max_price = priceRange[1];
      if (minRating > 0) params.min_rating = minRating;
      
      const data = await productsAPI.getAll(params);
      
      let sortedData = [...data];
      if (sortBy === 'price-low') {
        sortedData.sort((a, b) => a.price - b.price);
      } else if (sortBy === 'price-high') {
        sortedData.sort((a, b) => b.price - a.price);
      } else if (sortBy === 'rating') {
        sortedData.sort((a, b) => (b.rating || 0) - (a.rating || 0));
      }
      
      setProducts(sortedData);
    } catch (error) {
      console.error('Error loading products:', error);
      toast.error('Failed to load products');
    } finally {
      setLoading(false);
    }
  };

  const handleAddToCart = async (e, productId) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!isAuthenticated) {
      toast.error('Please login to add items to cart');
      navigate('/login');
      return;
    }
    
    try {
      await cartAPI.addToCart(productId, 1);
      toast.success('Added to cart');
    } catch (error) {
      toast.error('Failed to add to cart');
    }
  };

  return (
    <Layout>
      <div className="section-padding" data-testid="products-page">
        <div className="container-custom">
          {/* Header */}
          <div className="mb-12">
            <h1 className="text-4xl md:text-5xl font-bold mb-4">All Products</h1>
            <p className="text-muted-foreground text-lg">Discover our complete range of premium technology</p>
          </div>

          {/* Filters & Products */}
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
            {/* Mobile filter toggle - only shown below the lg breakpoint */}
            <div className="lg:hidden">
              <Button
                variant="outline"
                className="w-full flex items-center justify-center gap-2"
                onClick={() => setShowMobileFilters((prev) => !prev)}
                data-testid="mobile-filters-toggle"
              >
                {showMobileFilters ? <X className="w-4 h-4" /> : <SlidersHorizontal className="w-4 h-4" />}
                {showMobileFilters ? 'Hide Filters' : 'Show Filters'}
              </Button>
            </div>

            {/* Filter Sidebar - always visible on desktop (lg:block), only
                shown on mobile when explicitly toggled open */}
            <div className={`lg:col-span-1 ${showMobileFilters ? 'block' : 'hidden'} lg:block`}>
              <Card className="luxury-card lg:sticky lg:top-24">
                <CardContent className="p-6">
                  <h3 className="text-xl font-bold mb-6" style={{ color: 'var(--color-text-primary)' }}>
                    Filters
                  </h3>

                  {/* Brand Filter */}
                  <div className="mb-6">
                    <Label className="mb-3 block font-semibold">Brand</Label>
                    <Select value={brand} onValueChange={setBrand}>
                      <SelectTrigger data-testid="brand-filter">
                        <SelectValue placeholder="All Brands" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Brands</SelectItem>
                        {brands.map((b) => (
                          <SelectItem key={b} value={b}>{b}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Category Filter */}
                  <div className="mb-6">
                    <Label className="mb-3 block font-semibold">Category</Label>
                    <Select value={category} onValueChange={setCategory}>
                      <SelectTrigger data-testid="category-filter">
                        <SelectValue placeholder="Category" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Categories</SelectItem>
                        <SelectItem value="refurbished-laptops">Refurbished Laptops</SelectItem>
                        <SelectItem value="laptops">New Laptops</SelectItem>
                        <SelectItem value="desktops">Desktops</SelectItem>
                        <SelectItem value="phones">Phones</SelectItem>
                        <SelectItem value="security">Security</SelectItem>
                        <SelectItem value="printers">Printers</SelectItem>
                        <SelectItem value="accessories">Accessories</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Price Range */}
                  <div className="mb-6">
                    <Label className="mb-3 block font-semibold">Price Range</Label>
                    <div className="space-y-4">
                      <div className="flex items-center justify-between text-sm">
                        <span>KES {priceRange[0].toLocaleString()}</span>
                        <span>KES {priceRange[1].toLocaleString()}</span>
                      </div>
                      <input
                        type="range"
                        min="0"
                        max="500000"
                        step="10000"
                        value={priceRange[0]}
                        onChange={(e) => setPriceRange([parseInt(e.target.value), priceRange[1]])}
                        className="w-full"
                        data-testid="price-min-slider"
                      />
                      <input
                        type="range"
                        min="0"
                        max="500000"
                        step="10000"
                        value={priceRange[1]}
                        onChange={(e) => setPriceRange([priceRange[0], parseInt(e.target.value)])}
                        className="w-full"
                        data-testid="price-max-slider"
                      />
                    </div>
                  </div>

                  {/* Rating Filter */}
                  <div className="mb-6">
                    <Label className="mb-3 block font-semibold">Minimum Rating</Label>
                    <div className="space-y-2">
                      {[4, 3, 2, 1, 0].map((rating) => (
                        <button
                          key={rating}
                          onClick={() => setMinRating(rating)}
                          className={`w-full flex items-center justify-between p-2 rounded-lg transition-colors ${
                            minRating === rating
                              ? 'bg-accent text-white'
                              : 'hover:bg-secondary'
                          }`}
                          data-testid={`rating-filter-${rating}`}
                        >
                          <div className="flex items-center gap-1">
                            {[...Array(5)].map((_, i) => (
                              <Star
                                key={i}
                                size={16}
                                className={i < rating ? 'fill-current' : ''}
                              />
                            ))}
                          </div>
                          <span className="text-sm">{rating === 0 ? 'All' : `${rating}+ stars`}</span>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Clear Filters */}
                  <Button
                    variant="outline"
                    className="w-full"
                    onClick={() => {
                      setCategory('all');
                      setBrand('all');
                      setPriceRange([0, 500000]);
                      setMinRating(0);
                    }}
                    data-testid="clear-filters"
                  >
                    Clear All Filters
                  </Button>
                </CardContent>
              </Card>
            </div>

            {/* Products Grid */}
            <div className="lg:col-span-3">
              {/* Sort Options */}
              <div className="flex items-center justify-between mb-6">
                <p style={{ color: 'var(--color-text-secondary)' }}>
                  {products.length} products found
                </p>
                <Select value={sortBy} onValueChange={setSortBy}>
                  <SelectTrigger className="w-[200px]" data-testid="sort-filter">
                    <SelectValue placeholder="Sort by" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="featured">Featured</SelectItem>
                    <SelectItem value="price-low">Price: Low to High</SelectItem>
                    <SelectItem value="price-high">Price: High to Low</SelectItem>
                    <SelectItem value="rating">Highest Rated</SelectItem>
                  </SelectContent>
                </Select>
              </div>
          {loading ? (
            <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-6 lg:gap-8">
              {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
                <div key={i} className="skeleton h-64 sm:h-96 rounded-2xl"></div>
              ))}
            </div>
          ) : products.length === 0 ? (
            <div className="text-center py-16">
              <p className="text-muted-foreground text-lg">No products found in this category</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-6 lg:gap-8">
              {products.map((product) => (
                <div
                  key={product.product_id}
                  onClick={() => navigate(`/product/${product.product_id}`)}
                  className="cursor-pointer"
                  data-testid={`product-card-${product.product_id}`}
                >
                  <Card className="card-product h-full">
                    <div className="aspect-square overflow-hidden">
                      <img
                        src={product.images[0] || 'https://images.unsplash.com/photo-1518117823675-9a4bb23df61c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzR8MHwxfHNlYXJjaHwzfHxtaW5pbWFsaXN0JTIwbGFwdG9wJTIwd29ya3NwYWNlJTIwd2hpdGUlMjBkZXNrfGVufDB8fHx8MTc2NzcyNzg2NXww&ixlib=rb-4.1.0&q=85'}
                        alt={product.name}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <CardContent className="p-3 sm:p-6">
                      <div className="hidden sm:flex items-center gap-1 mb-2">
                        {[1, 2, 3, 4, 5].map((star) => (
                          <Star key={star} size={14} className="fill-accent text-accent" />
                        ))}
                        <span className="text-sm text-muted-foreground ml-2">(48)</span>
                      </div>
                      <h3 className="font-semibold text-sm sm:text-lg mb-1 sm:mb-2 line-clamp-2">{product.name}</h3>
                      <p className="hidden sm:block text-muted-foreground text-sm mb-4 line-clamp-1">{hintText(product.description)}</p>
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-base sm:text-2xl font-bold font-mono truncate">KES {product.price.toLocaleString()}</span>
                        <Button
                          size="sm"
                          className="btn-primary shrink-0 px-2.5 sm:px-4 text-xs sm:text-sm"
                          onClick={(e) => handleAddToCart(e, product.product_id)}
                          data-testid={`add-to-cart-${product.product_id}`}
                        >
                          Add
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              ))}
            </div>
          )}
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default ProductsPage;